import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import {
  FaHome, FaCar, FaUsers, FaChartBar, FaCog, FaFileContract,
  FaCreditCard, FaCalendarAlt, FaCalculator, FaShoppingCart, FaBell,
  FaUserShield, FaWrench, FaMoneyBillWave, FaBox, FaTools, FaDollarSign,
  FaCommentDots, FaMoon, FaSun, FaUser, FaSignOutAlt, FaExpand, FaCompress,
  FaShieldAlt, FaRss, FaSearch, FaChartLine, FaExternalLinkAlt, FaPalette,
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { FiX, FiMenu, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';

const THEME_LABELS = { dark: 'Dark', navy: 'Navy', light: 'Light' };

const SIDEBAR_W = 248;
const RAIL_W = 64;

// Isolated so the 1-second tick re-renders only this element, not the whole shell
const NavClock = ({ className }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return <p className={className}>{now.toLocaleTimeString()}</p>;
};

/* Tab registry — unchanged from the previous top-bar nav. Every tab key and
   every grouping is preserved; only the presentation moved to a sidebar. */
const navItems = [
  {
    name: 'Dashboard',
    icon: FaHome,
    component: 'dashboard',
    dropdown: [
      { name: 'Overview', component: 'dashboard', icon: FaHome },
      { name: 'Analytics', component: 'analytics', icon: FaChartBar },
      { name: 'Reports', component: 'reports', icon: FaChartLine },
    ],
  },
  {
    name: 'Inventory',
    icon: FaBox,
    component: 'unit-type',
    dropdown: [
      { name: 'Vehicles', component: 'vehicles', icon: FaCar },
      { name: 'Golf Carts', component: 'golf-carts', icon: FaCar },
      { name: 'Trailers', component: 'trailers', icon: FaCar },
      { name: 'Boats', component: 'boats', icon: FaCar },
    ],
  },
  {
    name: 'Deals',
    icon: FaFileContract,
    component: 'deals',
    dropdown: [
      { name: 'Deal Management', component: 'deals', icon: FaFileContract },
      { name: 'Deal Builder', component: 'deal-builder', icon: HiSparkles },
      { name: 'Quick Deal Calculator', component: 'quick-deal', icon: FaCalculator },
      { name: 'Credit Applications', component: 'credit-apps', icon: FaFileContract },
      { name: 'Pull Credit Report', component: 'credit-check', icon: FaCreditCard },
      { name: 'Authorization Log', component: 'credit-consent', icon: FaShieldAlt },
      { name: 'Contracts', component: 'contracts', icon: FaFileContract },
      { name: 'Forms', component: 'forms', icon: FaFileContract },
    ],
  },
  { name: 'Service', icon: FaTools, component: 'service' },
  { name: 'Leads', icon: FaUsers, component: 'leads' },
  {
    name: 'Payments',
    icon: FaDollarSign,
    component: 'payments',
    dropdown: [
      { name: 'Payment Management', component: 'payments', icon: FaCreditCard },
      { name: 'BHPH Accounts', component: 'bhph', icon: FaMoneyBillWave },
      { name: 'Recurring Payments', component: 'subscriptions', icon: FaCalendarAlt },
      { name: 'Test Authorize.Net', component: 'payment-test', icon: FaShieldAlt },
    ],
  },
  { name: 'Messages', icon: FaCommentDots, component: 'sms' },
  { name: 'Parts Store', icon: FaShoppingCart, component: 'parts-store' },
  {
    name: 'Settings',
    icon: FaCog,
    component: 'settings',
    dropdown: [
      { name: 'General Settings', component: 'settings', icon: FaCog },
      { name: 'Credit Bureau (700Credit)', component: 'credit-settings', icon: FaCreditCard },
      { name: '2FA Security', component: 'security', icon: FaUserShield },
      { name: 'VIN Decoder', component: 'vin-decoder', icon: FaSearch },
      { name: 'Google VLA Feed', component: 'google-vla', icon: FaRss },
    ],
  },
];

/* Tabs reachable from the switch in Admin.jsx but not surfaced in the nav —
   listed only so the top bar can still title them correctly. */
const EXTRA_TITLES = { 'ai-chat': { name: 'AI Chat', group: null } };

const titleIndex = (() => {
  const map = { ...EXTRA_TITLES };
  navItems.forEach((item) => {
    if (item.dropdown) {
      item.dropdown.forEach((sub) => {
        map[sub.component] = { name: sub.name, group: item.name, icon: sub.icon };
      });
    } else {
      map[item.component] = { name: item.name, group: null, icon: item.icon };
    }
  });
  return map;
})();

const isGroupActive = (item, activeTab) =>
  item.component === activeTab ||
  (item.dropdown && item.dropdown.some((d) => d.component === activeTab));

const IconAction = ({ label, onClick, children, badge }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="relative flex items-center justify-center w-8 h-8 rounded-adm-ctl text-adm-text-mid
      hover:bg-adm-bg-3 hover:text-adm-text-hi transition-colors duration-150"
  >
    {children}
    {badge > 0 && (
      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-adm-danger
        text-white text-[10px] font-semibold flex items-center justify-center tabular-nums">
        {badge}
      </span>
    )}
  </button>
);

/* -------------------------------------------------------------------------- */

const AdminNavigationModern = ({
  activeTab,
  setActiveTab,
  theme = 'dark',
  nextTheme = 'light',
  onCycleTheme,
  children,
}) => {
  const { signOut, user } = useAuth();
  const shellRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('pp-admin-rail') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('pp-admin-rail', collapsed ? '1' : '0');
    } catch {
      /* storage disabled — collapse still works for the session */
    }
  }, [collapsed]);

  // Close the profile menu on outside click or Escape; close the mobile drawer on Escape
  useEffect(() => {
    const onPointerDown = (e) => {
      if (shellRef.current && !shellRef.current.contains(e.target)) setProfileOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setProfileOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const go = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  // Search filters the nav: a group survives if it or any of its children match
  const filteredNavItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems
      .map((item) => {
        const selfMatch = item.name.toLowerCase().includes(q);
        const kids = item.dropdown?.filter((sub) => sub.name.toLowerCase().includes(q));
        if (selfMatch) return item;
        if (kids && kids.length) return { ...item, dropdown: kids };
        return null;
      })
      .filter(Boolean);
  }, [searchQuery]);

  const current = titleIndex[activeTab] || { name: 'Dashboard', group: null };

  /* ---- sidebar body (shared by the fixed rail and the mobile drawer) ---- */
  const renderNav = (railMode) => (
    <nav className="flex-1 overflow-y-auto px-2 pb-4" aria-label="Admin sections">
      {filteredNavItems.map((item) => {
        if (!item.dropdown) {
          const active = activeTab === item.component;
          return (
            <button
              key={item.component}
              type="button"
              onClick={() => go(item.component)}
              aria-current={active ? 'page' : undefined}
              aria-label={railMode ? item.name : undefined}
              title={railMode ? item.name : undefined}
              className={`group relative w-full flex items-center gap-2.5 rounded-adm-ctl mb-0.5
                ${railMode ? 'justify-center px-0 py-2' : 'px-2.5 py-1.5'}
                text-[13px] transition-colors duration-150
                ${active
                  ? 'bg-adm-blue-tint text-adm-text-hi font-medium'
                  : 'text-adm-text-mid hover:bg-adm-bg-3 hover:text-adm-text-hi'}`}
            >
              {active && (
                <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-adm-blue" aria-hidden="true" />
              )}
              <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
              {!railMode && <span className="truncate">{item.name}</span>}
            </button>
          );
        }

        return (
          <div key={item.component} className="mb-3">
            {railMode ? (
              <div className="my-2 mx-2 h-px bg-adm-border" aria-hidden="true" />
            ) : (
              <p className="px-2.5 pt-2 pb-1 text-[11px] font-medium text-adm-text-lo">
                {item.name}
              </p>
            )}
            {item.dropdown.map((sub) => {
              const active = activeTab === sub.component;
              const SubIcon = sub.icon || item.icon;
              return (
                <button
                  key={sub.component}
                  type="button"
                  onClick={() => go(sub.component)}
                  aria-current={active ? 'page' : undefined}
                  aria-label={railMode ? `${item.name}: ${sub.name}` : undefined}
                  title={railMode ? `${item.name}: ${sub.name}` : undefined}
                  className={`group relative w-full flex items-center gap-2.5 rounded-adm-ctl mb-0.5
                    ${railMode ? 'justify-center px-0 py-2' : 'px-2.5 py-1.5'}
                    text-[13px] transition-colors duration-150
                    ${active
                      ? 'bg-adm-blue-tint text-adm-text-hi font-medium'
                      : 'text-adm-text-mid hover:bg-adm-bg-3 hover:text-adm-text-hi'}`}
                >
                  {active && (
                    <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-adm-blue" aria-hidden="true" />
                  )}
                  <SubIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  {!railMode && <span className="truncate">{sub.name}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
      {filteredNavItems.length === 0 && !railMode && (
        <p className="px-2.5 py-6 text-xs text-adm-text-lo text-center">
          No sections match “{searchQuery}”.
        </p>
      )}
    </nav>
  );

  const brand = (railMode) => (
    <div className={`flex items-center gap-2.5 h-14 shrink-0 ${railMode ? 'justify-center px-0' : 'px-4'}`}>
      <img src="/assets/assets/images/logo.webp" alt="" className="h-7 w-auto" aria-hidden="true" />
      {!railMode && (
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-adm-text-hi leading-tight">Paradise Performance</p>
          <p className="text-[11px] text-adm-text-lo leading-tight">Admin</p>
        </div>
      )}
    </div>
  );

  return (
    <div ref={shellRef}>
      {/* ---------------- Fixed sidebar (lg and up) ---------------- */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 z-40 flex-col bg-adm-bg-1
          border-r border-adm-border transition-[width] duration-200"
        style={{ width: collapsed ? RAIL_W : SIDEBAR_W }}
      >
        {brand(collapsed)}

        {!collapsed && (
          <div className="px-3 pb-3">
            <label htmlFor="admin-nav-search" className="sr-only">Search admin sections</label>
            <div className="relative">
              <FaSearch
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-adm-text-lo"
                aria-hidden="true"
              />
              <input
                id="admin-nav-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sections"
                className="w-full !pl-8 !pr-2 !py-1.5 text-[13px]"
              />
            </div>
          </div>
        )}

        {renderNav(collapsed)}

        <div className="shrink-0 border-t border-adm-border p-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`w-full flex items-center gap-2.5 rounded-adm-ctl px-2.5 py-1.5 text-[13px]
              text-adm-text-mid hover:bg-adm-bg-3 hover:text-adm-text-hi transition-colors duration-150
              ${collapsed ? 'justify-center' : ''}`}
          >
            {collapsed ? <FiChevronsRight className="w-4 h-4" /> : <FiChevronsLeft className="w-4 h-4" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* ---------------- Mobile drawer ----------------
          Mounted conditionally rather than through AnimatePresence: the exit
          animation completed but the node was never removed, leaving an
          invisible fixed overlay at z-9999 that swallowed every click. The
          entry animation is kept; closing is immediate. */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[9999] lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            className="absolute inset-y-0 left-0 w-[86%] max-w-xs bg-adm-bg-1 border-r border-adm-border
              flex flex-col shadow-adm-pop"
            role="dialog"
            aria-label="Admin navigation"
          >
              <div className="flex items-center justify-between pr-2">
                {brand(false)}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close navigation"
                  className="w-8 h-8 flex items-center justify-center rounded-adm-ctl text-adm-text-mid hover:bg-adm-bg-3"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <div className="px-3 pb-3">
                <label htmlFor="admin-nav-search-mobile" className="sr-only">Search admin sections</label>
                <input
                  id="admin-nav-search-mobile"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search sections"
                  className="w-full text-[13px]"
                />
              </div>

              {renderNav(false)}

              <div className="shrink-0 border-t border-adm-border p-3 space-y-1">
                <p className="px-1 text-[11px] text-adm-text-lo truncate">{user?.email}</p>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2.5 rounded-adm-ctl px-2.5 py-2 text-[13px]
                    text-adm-danger hover:bg-adm-danger/10 transition-colors"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
          </motion.div>
        </div>
      )}

      {/* ---------------- Content column ---------------- */}
      <div
        className="pp-admin-content transition-[padding] duration-200"
        style={{ '--adm-sidebar-w': `${collapsed ? RAIL_W : SIDEBAR_W}px` }}
      >
        {/* Sticky top bar */}
        <header
          className="sticky top-0 z-30 bg-adm-bg-0/85 backdrop-blur-md border-b border-adm-border"
        >
          <div className="flex items-center gap-3 h-14 px-3 sm:px-5">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation"
              className="lg:hidden w-8 h-8 flex items-center justify-center rounded-adm-ctl
                text-adm-text-mid hover:bg-adm-bg-3 hover:text-adm-text-hi transition-colors"
            >
              <FiMenu className="w-4 h-4" />
            </button>

            <div className="min-w-0 flex-1">
              {current.group && (
                <p className="text-[11px] text-adm-text-lo leading-tight">{current.group}</p>
              )}
              <h1 className="text-[15px] font-semibold text-adm-text-hi truncate leading-tight">
                {current.name}
              </h1>
            </div>

            <NavClock className="hidden md:block text-[11px] text-adm-text-lo tabular-nums mr-1" />

            <IconAction label="Notifications" badge={notifications}>
              <FaBell className="w-3.5 h-3.5" />
            </IconAction>

            <IconAction
              label={`Theme: ${THEME_LABELS[theme] || theme}. Switch to ${THEME_LABELS[nextTheme] || nextTheme}.`}
              onClick={onCycleTheme}
            >
              {theme === 'light' ? (
                <FaSun className="w-3.5 h-3.5" />
              ) : theme === 'navy' ? (
                <FaPalette className="w-3.5 h-3.5" />
              ) : (
                <FaMoon className="w-3.5 h-3.5" />
              )}
            </IconAction>

            <IconAction
              label={fullscreen ? 'Exit full screen' : 'Enter full screen'}
              onClick={toggleFullscreen}
            >
              {fullscreen ? <FaCompress className="w-3.5 h-3.5" /> : <FaExpand className="w-3.5 h-3.5" />}
            </IconAction>

            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                aria-label="Account menu"
                className="flex items-center gap-2 h-8 pl-1 pr-2 rounded-adm-ctl
                  hover:bg-adm-bg-3 transition-colors duration-150"
              >
                <span className="w-6 h-6 rounded-full bg-adm-blue-tint flex items-center justify-center">
                  <FaUser className="w-3 h-3 text-adm-blue-text" aria-hidden="true" />
                </span>
                <span className="hidden sm:block text-[13px] text-adm-text-mid max-w-[10rem] truncate">
                  {user?.email?.split('@')[0] || 'Admin'}
                </span>
              </button>

              {profileOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.14, ease: 'easeOut' }}
                  role="menu"
                    className="absolute right-0 top-full mt-2 w-60 rounded-adm-card bg-adm-bg-2
                      shadow-adm-pop overflow-hidden"
                  >
                    <div className="px-3 py-2.5 border-b border-adm-border">
                      <p className="text-[13px] text-adm-text-hi truncate">{user?.email}</p>
                      <p className="text-[11px] text-adm-text-lo">Administrator</p>
                    </div>
                    <a
                      href="/"
                      role="menuitem"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-[13px] text-adm-text-mid
                        hover:bg-adm-bg-3 hover:text-adm-text-hi transition-colors"
                    >
                      <FaExternalLinkAlt className="w-3 h-3" aria-hidden="true" />
                      View Site
                    </a>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setActiveTab('settings');
                        setProfileOpen(false);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-adm-text-mid
                        hover:bg-adm-bg-3 hover:text-adm-text-hi transition-colors text-left"
                    >
                      <FaCog className="w-3 h-3" aria-hidden="true" />
                      Settings
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-adm-danger
                        hover:bg-adm-danger/10 transition-colors text-left border-t border-adm-border"
                    >
                      <FaSignOutAlt className="w-3 h-3" aria-hidden="true" />
                      Sign Out
                    </button>
                </motion.div>
              )}
            </div>
          </div>
        </header>

        {children}
      </div>
    </div>
  );
};

export default AdminNavigationModern;
